import { useEffect, useState } from 'react';
import Button from '@/components/UI/Button';
import FormNumberInput from '@/components/UI/FormNumberInput';
import FormSelect from '@/components/UI/FormSelect';
import type { TradeLogicJsonObject, TradeLogicJsonValue } from '../model';
import type { SequencePriceCaptureTargetOption } from './sequencePriceCaptures';
import {
    buildPriceExecution,
    defaultPriceExecutionDraft,
    isStructuralPriceTargetSource,
    isNamedPriceTargetSource,
    isRollingPriceTarget,
    namedPriceTargetLabels,
    priceTargetDurations,
    readPriceExecutionDraft,
    type PriceExecutionDraft,
    type PriceTargetSource
} from './priceExecutionDraft';

interface Props {
    value: TradeLogicJsonValue | undefined;
    onApply: (execution: TradeLogicJsonObject | null) => void;
    captureOptions?: SequencePriceCaptureTargetOption[];
}

const sourceOptions: { value: PriceTargetSource; label: string }[] = [
    { value: 'literal', label: 'Fixed price' },
    { value: 'capture', label: 'Captured sequence price' },
    { value: 'ema', label: 'Completed-bar EMA' },
    { value: 'sma', label: 'Completed-bar SMA' },
    { value: 'vwap', label: 'Completed-bar session VWAP' },
    { value: 'high', label: 'Completed candle high' },
    { value: 'low', label: 'Completed candle low' },
    { value: 'open', label: 'Completed candle open' },
    { value: 'close', label: 'Completed candle close' },
    { value: 'nearestPivot', label: 'Nearest pivot level' },
    { value: 'nearestSupport', label: 'Nearest support level' },
    { value: 'nearestResistance', label: 'Nearest resistance level' },
    { value: 'nearestFibonacci', label: 'Nearest Fibonacci level' },
    ...Object.entries(namedPriceTargetLabels).flatMap(([key, label]) => (isNamedPriceTargetSource(key) ? [{ value: key, label }] : []))
];

const PriceExecutionEditor = ({ value, onApply, captureOptions = [] }: Props) => {
    const [enabled, setEnabled] = useState(value !== undefined && value !== null);
    const [draft, setDraft] = useState<PriceExecutionDraft | null>(() =>
        value == null ? defaultPriceExecutionDraft() : readPriceExecutionDraft(value)
    );
    const [error, setError] = useState<string | null>(null);
    const simpleCancellation = draft?.cancellation && draft.cancellation !== 'advanced' ? draft.cancellation : null;
    const isStructuralTarget = draft ? isStructuralPriceTargetSource(draft.source) : false;
    const isNamedTarget = draft ? isNamedPriceTargetSource(draft.source) : false;
    const frozenSource = isStructuralTarget || isNamedTarget || draft?.source === 'capture';
    const livePaperReasons = draft
        ? [
              ...(draft.targetPolicy !== 'freeze_at_arm' ? ['target refresh'] : []),
              ...(draft.cancellation !== null ? ['cancellation condition'] : []),
              ...(isStructuralTarget ? ['structural target'] : [])
          ]
        : [];

    useEffect(() => {
        setEnabled(value !== undefined && value !== null);
        setDraft(value == null ? defaultPriceExecutionDraft() : readPriceExecutionDraft(value));
        setError(null);
    }, [value]);

    const update = (changes: Partial<PriceExecutionDraft>) => setDraft((current) => (current ? { ...current, ...changes } : current));
    const apply = () => {
        if (!draft) return;
        try {
            onApply(buildPriceExecution(draft, value));
            setError(null);
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : 'Unable to save waiting entry.');
        }
    };

    return (
        <div className="mt-4 rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <div className="text-sm font-semibold text-zinc-100">Wait for price</div>
                    <div className="mt-1 text-xs text-zinc-500">
                        Underlying entries wait for the first eligible one-minute touch after the setup completes.
                    </div>
                </div>
                {enabled ? (
                    <Button
                        size="sm"
                        variant="outline-dark"
                        onClick={() => {
                            onApply(null);
                            setEnabled(false);
                            setDraft(defaultPriceExecutionDraft());
                        }}
                    >
                        Use standard entry
                    </Button>
                ) : (
                    <Button size="sm" variant="outline-dark" onClick={() => setEnabled(true)}>
                        Add price wait
                    </Button>
                )}
            </div>
            {enabled && draft === null && (
                <div className="mt-3 text-xs text-amber-200">
                    This saved target uses an advanced expression. It is preserved; edit it programmatically or remove the price wait to
                    start over.
                </div>
            )}
            {enabled && draft && (
                <>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <FormSelect
                            label="Order behavior"
                            options={[
                                { value: 'limit', label: 'Limit: touch at or better' },
                                { value: 'stop', label: 'Stop: cross or gap through' }
                            ]}
                            value={draft.orderType}
                            onChange={(event) => update({ orderType: event.target.value === 'stop' ? 'stop' : 'limit' })}
                        />
                        <FormSelect
                            label="Target source"
                            options={sourceOptions}
                            value={draft.source}
                            onChange={(event) => {
                                const source = sourceOptions.find((option) => option.value === event.target.value)?.value;
                                if (source)
                                    update({
                                        source,
                                        ...(source === 'capture'
                                            ? {
                                                  captureKey: captureOptions[0]?.key ?? null,
                                                  duration: captureOptions[0]?.duration ?? draft.duration,
                                                  targetPolicy: 'freeze_at_arm' as const,
                                                  oncePerSource: false
                                              }
                                            : { captureKey: null }),
                                        ...(isStructuralPriceTargetSource(source) || isNamedPriceTargetSource(source)
                                            ? { targetPolicy: 'freeze_at_arm' as const }
                                            : { oncePerSource: false }),
                                        ...(!priceTargetDurations(source).includes(draft.duration)
                                            ? { duration: priceTargetDurations(source)[0] }
                                            : {}),
                                        ...(source === 'vwap' && ['1d', '1w', '1mo'].includes(draft.duration) ? { duration: '60m' } : {})
                                    });
                            }}
                        />
                        {draft.source === 'capture' ? (
                            <FormSelect
                                label="Saved sequence capture"
                                options={[
                                    { value: '', label: 'Select a capture' },
                                    ...captureOptions.map((option) => ({ value: option.key, label: option.label }))
                                ]}
                                value={draft.captureKey ?? ''}
                                onChange={(event) => {
                                    const capture = captureOptions.find((option) => option.key === event.target.value);
                                    update({ captureKey: capture?.key ?? null, ...(capture ? { duration: capture.duration } : {}) });
                                }}
                            />
                        ) : draft.source === 'literal' ? (
                            <FormNumberInput
                                label="Target price"
                                min={0.000001}
                                step={0.01}
                                value={draft.literalPrice}
                                onValueChange={(literalPrice) => update({ literalPrice })}
                            />
                        ) : (
                            <FormSelect
                                label="Reference timeframe"
                                options={priceTargetDurations(draft.source).map((duration) => ({ value: duration, label: duration }))}
                                value={draft.duration}
                                onChange={(event) => update({ duration: event.target.value })}
                            />
                        )}
                        {(draft.source === 'ema' || draft.source === 'sma') && (
                            <FormNumberInput
                                label="Moving-average period"
                                integer
                                min={1}
                                max={1000}
                                value={draft.period}
                                onValueChange={(period) => update({ period })}
                            />
                        )}
                        {draft.source !== 'literal' &&
                            draft.source !== 'capture' &&
                            draft.source !== 'ema' &&
                            draft.source !== 'sma' &&
                            draft.source !== 'vwap' &&
                            !frozenSource && (
                                <FormNumberInput
                                    label="Completed bars back"
                                    helpText="0 is the candle that just completed; 1 is the candle before it."
                                    integer
                                    min={0}
                                    max={6}
                                    value={draft.offsetBars}
                                    onValueChange={(offsetBars) => update({ offsetBars })}
                                />
                            )}
                        {isRollingPriceTarget(draft.source) && (
                            <FormNumberInput
                                label="Completed bars in range"
                                integer
                                min={1}
                                max={200}
                                value={draft.rangeBars}
                                onValueChange={(rangeBars) => update({ rangeBars })}
                            />
                        )}
                        <FormSelect
                            label="Trigger price"
                            options={[
                                { value: 'touch', label: 'Touch the target' },
                                { value: 'above', label: 'First price increment above' },
                                { value: 'below', label: 'First price increment below' }
                            ]}
                            value={draft.breakDirection}
                            onChange={(event) => {
                                const breakDirection = event.target.value;
                                if (breakDirection === 'touch' || breakDirection === 'above' || breakDirection === 'below')
                                    update({ breakDirection, ...(breakDirection === 'touch' ? {} : { orderType: 'stop' }) });
                            }}
                        />
                        {draft.breakDirection !== 'touch' && (
                            <FormNumberInput
                                label="Instrument price increment"
                                helpText="Enter the valid price increment for the instrument being tested; it is not inferred. For example, 0.01 for a cent increment."
                                min={0.000001}
                                step={0.000001}
                                value={draft.priceIncrement}
                                onValueChange={(priceIncrement) => update({ priceIncrement })}
                            />
                        )}
                        {frozenSource && (
                            <FormSelect
                                label="Repeat entries"
                                options={[
                                    { value: 'repeat', label: 'Allow a new setup after completion' },
                                    { value: 'once', label: 'One waiting attempt per source' }
                                ]}
                                value={draft.oncePerSource ? 'once' : 'repeat'}
                                onChange={(event) => update({ oncePerSource: event.target.value === 'once' })}
                            />
                        )}
                        {isNamedTarget && (
                            <p className="text-xs text-slate-400">
                                Historical only. The level is frozen from completed evidence. Opening ranges must complete before the setup
                                arms. One attempt per source also counts waits that expire without a fill.
                            </p>
                        )}
                        <FormSelect
                            label="Price offset"
                            options={[
                                { value: 'none', label: 'At reference price' },
                                { value: 'absolute', label: 'Add price amount' },
                                { value: 'percent', label: 'Add percent' }
                            ]}
                            value={draft.offsetUnit ?? 'none'}
                            onChange={(event) => {
                                const unit = event.target.value;
                                update({
                                    offsetUnit: unit === 'absolute' || unit === 'percent' ? unit : null,
                                    offsetAmount: unit === 'none' ? null : (draft.offsetAmount ?? 0)
                                });
                            }}
                        />
                        {draft.offsetUnit !== null && (
                            <FormNumberInput
                                label={draft.offsetUnit === 'percent' ? 'Offset percent (2 = 2%)' : 'Offset price amount'}
                                helpText="Use a negative value to set the target below the reference."
                                step={draft.offsetUnit === 'percent' ? 0.1 : 0.01}
                                value={draft.offsetAmount}
                                onValueChange={(offsetAmount) => update({ offsetAmount })}
                            />
                        )}
                        <FormSelect
                            label="Target updates"
                            options={
                                frozenSource
                                    ? [{ value: 'freeze_at_arm', label: 'Freeze when setup completes' }]
                                    : [
                                          { value: 'freeze_at_arm', label: 'Freeze when setup completes' },
                                          { value: 'refresh_on_reference_close', label: 'Refresh after each reference close' }
                                      ]
                            }
                            value={draft.targetPolicy}
                            onChange={(event) =>
                                update({
                                    targetPolicy:
                                        event.target.value === 'refresh_on_reference_close' ? 'refresh_on_reference_close' : 'freeze_at_arm'
                                })
                            }
                        />
                        <FormNumberInput
                            label="Wait lifetime"
                            integer
                            min={1}
                            value={draft.lifetimeValue}
                            onValueChange={(lifetimeValue) => update({ lifetimeValue })}
                        />
                        <FormSelect
                            label="Lifetime unit"
                            options={[
                                { value: 'minutes', label: 'Minutes' },
                                { value: 'hours', label: 'Hours' },
                                { value: 'days', label: 'Days' }
                            ]}
                            value={draft.lifetimeUnit}
                            onChange={(event) => {
                                const lifetimeUnit = event.target.value;
                                if (lifetimeUnit === 'minutes' || lifetimeUnit === 'hours' || lifetimeUnit === 'days') {
                                    update({ lifetimeUnit });
                                }
                            }}
                        />
                    </div>
                    {draft.cancellation === 'advanced' ? (
                        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-amber-200">
                            <span>An advanced cancellation condition is saved and will be preserved.</span>
                            <Button size="sm" variant="outline-dark" onClick={() => update({ cancellation: null })}>
                                Remove cancellation
                            </Button>
                        </div>
                    ) : (
                        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                            <FormSelect
                                label="Cancel waiting entry"
                                options={[
                                    { value: 'none', label: 'Only at lifetime expiry' },
                                    { value: 'below', label: 'If completed close is below price' },
                                    { value: 'above', label: 'If completed close is above price' }
                                ]}
                                value={simpleCancellation === null ? 'none' : simpleCancellation.operator === '<' ? 'below' : 'above'}
                                onChange={(event) => {
                                    const choice = event.target.value;
                                    if (choice === 'none') update({ cancellation: null });
                                    if (choice === 'below' || choice === 'above') {
                                        update({
                                            cancellation: {
                                                operator: choice === 'below' ? '<' : '>',
                                                ...(simpleCancellation && 'indicator' in simpleCancellation
                                                    ? { indicator: simpleCancellation.indicator, period: simpleCancellation.period }
                                                    : { price: simpleCancellation?.price ?? null })
                                            }
                                        });
                                    }
                                }}
                            />
                            {simpleCancellation && (
                                <>
                                    <FormSelect
                                        label="Compare completed close with"
                                        options={[
                                            { value: 'price', label: 'Fixed price' },
                                            { value: 'ema', label: 'Completed-bar EMA' },
                                            { value: 'sma', label: 'Completed-bar SMA' }
                                        ]}
                                        value={'indicator' in simpleCancellation ? simpleCancellation.indicator : 'price'}
                                        onChange={(event) => {
                                            const choice = event.target.value;
                                            update({
                                                cancellation:
                                                    choice === 'ema' || choice === 'sma'
                                                        ? { operator: simpleCancellation.operator, indicator: choice, period: 8 }
                                                        : { operator: simpleCancellation.operator, price: null }
                                            });
                                        }}
                                    />
                                    {'indicator' in simpleCancellation ? (
                                        <FormNumberInput
                                            label="Cancellation moving-average period"
                                            integer
                                            min={1}
                                            max={1000}
                                            value={simpleCancellation.period}
                                            onValueChange={(period) => update({ cancellation: { ...simpleCancellation, period } })}
                                        />
                                    ) : (
                                        <FormNumberInput
                                            label="Cancellation close price"
                                            min={0.000001}
                                            step={0.01}
                                            value={simpleCancellation.price}
                                            onValueChange={(price) => update({ cancellation: { ...simpleCancellation, price } })}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}
                    <div className="mt-1 text-xs text-zinc-500">
                        Orders become eligible at the next minute boundary. References use completed candles; a stop may fill beyond its
                        trigger after a gap. Simple cancellation checks the selected reference timeframe only after its candle completes.
                        Portfolio simulation supports frozen long buy limits and stops. A stop gap exceeding unreserved cash makes the run
                        unsupported.
                    </div>
                    <div className={`mt-2 text-xs ${livePaperReasons.length === 0 ? 'text-emerald-300' : 'text-amber-200'}`}>
                        {livePaperReasons.length === 0
                            ? 'This order shape can be considered for automatic live-paper execution. Captured targets additionally require a valid completed-bar sequence; the strategy must pass the monitored-timeframe, completed-bar, full-size, single-underlying, owned-paper-account readiness checks after publishing. Native stop entries require a supported paper broker (currently TradeStation).'
                            : `Historical research only with the current settings (${livePaperReasons.join(', ')}).`}
                    </div>
                    {error && <div className="mt-3 text-xs text-red-200">{error}</div>}
                    <div className="mt-3">
                        <Button size="sm" variant="secondary" onClick={apply}>
                            Apply price wait
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default PriceExecutionEditor;
